import { useTranslate } from 'hooks/useTranslate';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import ReactToPrint from 'react-to-print';
import { State } from 'redux/initialState';

import operationTypes from 'constants/operationTypes';
import { SocietyCorporateId } from 'types/Society';

import OperationMenuDots from 'modules/_shared/components/MenuDots/OperationMenuDots';
import PageWithTabs from 'modules/_shared/components/Pages/PageWithTabs';
import Button from 'modules/_shared/components/Buttons';

import { useOperationDetailsPage } from '../context/OperationDetailsPageContext';
import documentsDropdownOptions from '../constants/documentsDropdownOptions';

import { getDocumentTitle } from '../utils/getDocumentTitle';

import OperationInfoSection from './OperationInfoSection';
import OperationDetailsDocumentsTab from './OperationDetailsDocumentsTab';
import OperationDetailsDataTab from './OperationDetailsDataTab';

const OperationDetailsPageBody = () => {
  const { t } = useTranslate();

  const printRef = useRef(null);
  const { data, actions } = useOperationDetailsPage();

  const isPlatformAXA =
    process.env.REACT_APP_UI_CORPORATE_ID === SocietyCorporateId.AXA;

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const tabs = [
    {
      key: 'details-tab',
      title: t('Details'),
      icon: 'ni-list-index-fill',
      content: <OperationDetailsDataTab />,
    },
    {
      key: 'documents-tab',
      title: t('Scriptures'),
      icon: 'ni-file-docs',
      content: <OperationDetailsDocumentsTab />,
    },
  ];

  const hasSEPAOption =
    actualSociety?.configuration?.hasSEPAOption && !data.isOldOperation;
  const hasTaxTemplate =
    (actualSociety?.templates?.tax2024 && !data.isOldOperation) ||
    (actualSociety?.templates?.tax2025 && !data.isOldOperation);
  const hasDividendTemplate = !data.isOldOperation;

  const hasDividendFiscalDocument = !data.isOldOperation && isPlatformAXA;
  const hasDividendRequestDocument = !data.isOldOperation && isPlatformAXA;

  return (
    <div className="nk-block">
      <div className="invoice">
        <div className="invoice-action d-flex align-items-center justify-content-between">
          {data.operation?.operationType ===
            operationTypes.DIVIDENDS_DISTRIBUTION && (
            <>
              <div className="mr-2">
                <Button
                  onClick={actions.handleNotifyPartners}
                  disabled={data.isOldOperation && !isPlatformAXA}
                >
                  <em className="icon ni ni-send mr-2" />
                  {t('NotifyPartners')}
                </Button>
              </div>

              <div className="mr-2">
                <OperationMenuDots
                  title={t('Download')}
                  menuOptions={documentsDropdownOptions}
                  params={{
                    hasSEPAOption,
                    hasTaxTemplate,
                    hasDividendTemplate,
                    hasDividendFiscalDocument,
                    hasDividendRequestDocument,
                    handleGenerateBankDocument:
                      actions.handleGenerateBankDocument,
                    handleGenerateTaxDeclaration:
                      actions.handleGenerateTaxDeclaration,
                    handleGenerateTax193: actions.handleGenerateTax193,
                    handleOpenDividendModal: actions.handleOpenDividendModal,
                    handleDownloadDividendFiscalDocument:
                      actions.handleDownloadDividendFiscalDocument,
                    handleDownloadDividendRequestDocument:
                      actions.handleDownloadDividendRequestDocument,
                  }}
                  disabled={data.isOldOperation}
                  handleModal={null}
                />
              </div>
            </>
          )}

          <ReactToPrint
            trigger={() => (
              <a className="btn btn-icon btn-lg btn-white btn-dim btn-outline-primary">
                <em className="icon ni ni-printer-fill" />
              </a>
            )}
            content={() => printRef.current || null}
            documentTitle={getDocumentTitle(actualSociety, data.operation)}
          />
        </div>

        <div className="invoice-wrap" ref={printRef}>
          <OperationInfoSection />
          <PageWithTabs tabs={tabs} />
        </div>
      </div>
    </div>
  );
};

export default OperationDetailsPageBody;
